import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatch, useNavigate, useParams } from 'react-router-dom';

import { type WorkflowTemplate, workflowsApi } from '@/api/workflows';
import { getTabId } from '@/auth/tabRegistry';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

import { forgetEditingWorkflow, rememberEditingWorkflow } from './editorLock';
import { extractApiError } from './statusUtils';

/**
 * Why this tab lost (or never got) write access.
 *
 * Both outcomes are "read-only" to the code and completely different to the person at the
 * keyboard, so they get distinct copy — see {@link lockBannerCopy}.
 *   - `heldByOther`: a live lock belongs to a colleague, either because they got there first
 *     or because the owner reclaimed the workflow mid-session.
 *   - `expired`: nobody holds it; this tab's own hold simply aged past the server TTL.
 */
export type LockBlockReason = 'heldByOther' | 'expired';

/**
 * State that drives the read-only banner. Non-null means the editor on this tab can't write,
 * and we surface the banner so the user knows up front. `holderName` / `expiresAt` are only
 * populated for `heldByOther` — on an expiry there is no holder to name.
 */
export type LockBlockState = {
  reason: LockBlockReason;
  holderName?: string | null;
  expiresAt?: string | null;
};

export type WorkflowEditorSessionValue = {
  workflow: WorkflowTemplate | null;
  loading: boolean;
  /** When non-null, render read-only with the banner. */
  lockBlock: LockBlockState | null;
  /** Owner-only force-release: BE atomically clears the foreign lock + acquires it for the caller. */
  reclaim: () => Promise<void>;
  /** Patch the loaded workflow in-place after a successful editor mutation. */
  patchWorkflow: (fresh: WorkflowTemplate) => void;
};

const WorkflowEditorSessionContext = createContext<WorkflowEditorSessionValue | null>(null);

/**
 * Read the editing session the surrounding route provides. Throws outside the layout route,
 * which is a wiring bug rather than a runtime condition worth rendering around.
 */
export function useWorkflowEditorSession(): WorkflowEditorSessionValue {
  const ctx = useContext(WorkflowEditorSessionContext);
  if (!ctx) {
    throw new Error(
      'useWorkflowEditorSession must be used inside the <WorkflowEditorSession> layout route.',
    );
  }
  return ctx;
}

/**
 * How long a release waits before it actually fires, so a re-registration can cancel it.
 *
 * The lock now belongs to an editing SESSION rather than to a page, and React expresses
 * "same session, different page" as an unmount immediately followed by a mount. Two things
 * produce that shape here: <StrictMode>, which double-invokes every effect in development,
 * and a fast route change that briefly tears the provider down. Firing the DELETE eagerly in
 * either case races the re-acquire and can leave the editor sitting in an unlocked workflow.
 *
 * A quarter second is far longer than a synchronous remount and far shorter than a user
 * noticing; the server-side TTL covers anything that outlives it.
 */
const RELEASE_GRACE_MS = 250;

/** 20s lock-state poll. See the tradeoff note on the effect below. */
const POLL_MS = 20_000;

/**
 * Routes inside the editing session that must never ACQUIRE the lock.
 *
 * Preview and step-config are reachable from places that have nothing to do with editing —
 * the dashboard's "preview published version" link and the Archive page both deep-link
 * straight in. Acquiring on arrival would mean an originator glancing at a preview silently
 * takes the lock on a colleague's Draft and holds them out of their own work. They stay
 * inside the session so that stepping out to one mid-edit does not RELEASE the lock; they
 * simply never take one.
 */
function useIsHoldOnlyRoute(): boolean {
  const preview = useMatch('/originator/workflows/:id/preview');
  const stepConfig = useMatch('/originator/workflows/:id/steps/:stepId/config');
  return Boolean(preview || stepConfig);
}

/**
 * Owns the editor lock for one workflow across every page of its editing session.
 *
 * Replaces the old per-page `useEditorLock`, under which the lock belonged to a page mount:
 * navigating Details → Form Builder → PDF Mapping released and re-acquired at each step,
 * leaving the workflow genuinely unlocked between the two round trips, and stepping out to
 * preview or step-config surrendered it with nothing to take it back.
 *
 * Mounted as a layout route, this stays mounted for the whole session and releases once, on
 * the way back to the dashboard.
 */
export function WorkflowEditorSession() {
  const { id } = useParams();
  // Keyed so a switch from workflow A's editor straight to B's is a real unmount → remount.
  // React would otherwise reuse this element across the param change, holding A's provider
  // open and leaking A's lock for the full TTL.
  return <WorkflowEditorSessionInner key={id} templateId={id} />;
}

function WorkflowEditorSessionInner({ templateId }: { templateId: string | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // getTabId, not useTabSlot: the hook CLAIMS a slot, and claiming a second one for a tab
  // AuthGuard has already admitted spends two of the five slots on a single tab.
  const tabId = getTabId();
  const holdOnly = useIsHoldOnlyRoute();

  const [workflow, setWorkflow] = useState<WorkflowTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [lockBlock, setLockBlock] = useState<LockBlockState | null>(null);
  const acquiredLockRef = useRef(false);

  // `navigate` and `t` are read through refs so they stay OUT of the load effect's deps.
  // useNavigate() returns a fresh identity on every location change, so depending on it
  // would refetch the workflow on each hop between pages of the session — exactly the
  // redundant round trips this layout route exists to remove.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const tRef = useRef(t);
  tRef.current = t;

  // Load the workflow once per session. Deliberately independent of `holdOnly`: arriving on
  // preview and clicking through to details must not refetch.
  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;

    (async () => {
      try {
        const loaded = await workflowsApi.get(templateId);
        if (cancelled) return;
        setWorkflow(loaded);
      } catch (err) {
        if (cancelled) return;
        message.error(extractApiError(err).message ?? tRef.current('workflow.details.loadFailed'));
        navigateRef.current('/originator/dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [templateId]);

  // Acquire + release. Depends on `holdOnly` so deep-linking to preview and then navigating
  // to an editing page still acquires at that point, rather than acquisition being dead for
  // the rest of the session.
  const editable = workflow?.status === 'Draft' || workflow?.status === 'Rejected';
  useEffect(() => {
    if (!templateId || !editable || holdOnly) return;
    if (acquiredLockRef.current) return;
    let cancelled = false;

    (async () => {
      try {
        const acquired = await workflowsApi.acquireLock(templateId);
        if (cancelled) return;
        setWorkflow(acquired);
        setLockBlock(null);
        rememberEditingWorkflow(templateId, tabId);
        acquiredLockRef.current = true;
      } catch (err) {
        if (cancelled) return;
        const apiErr = extractApiError(err);
        if (apiErr.status === 409 && apiErr.body && typeof apiErr.body === 'object') {
          // Couldn't take the lock — keep the content from the GET above and surface the
          // banner. Mutation buttons disable via lockedByOther downstream.
          //
          // Acquire can only ever lose to a LIVE foreign lock (a stale one is simply claimed),
          // so `heldByOther` is the right default when an older BE omits the reason.
          const lockState = (
            apiErr.body as {
              lockState?: { holderName?: string; expiresAt?: string; reason?: string };
            }
          ).lockState;
          setLockBlock({
            reason: lockState?.reason === 'expired' ? 'expired' : 'heldByOther',
            holderName: lockState?.holderName ?? null,
            expiresAt: lockState?.expiresAt ?? null,
          });
          return;
        }
        // Any other failure — surface it but don't bounce the user; the read-only content
        // from the load above is still useful.
        message.error(apiErr.message ?? tRef.current('workflow.details.loadFailed'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [templateId, editable, holdOnly, tabId]);

  // Release on leaving the session. Separate from the acquire effect so a `holdOnly` flip
  // (details → preview) cannot tear the lock down — only actually unmounting does.
  useEffect(() => {
    if (!templateId) return;
    return () => {
      if (!acquiredLockRef.current) return;
      acquiredLockRef.current = false;

      // Another tab still has this workflow open → that tab keeps the lock alive and this
      // one must not release it out from under them.
      const wasLastTab = forgetEditingWorkflow(templateId, tabId);
      if (!wasLastTab) return;

      const timer = window.setTimeout(() => {
        pendingReleases.delete(templateId);
        void workflowsApi.releaseLock(templateId).catch(() => {
          // Best-effort. The landing-page sweep retries and the TTL backstops it.
        });
      }, RELEASE_GRACE_MS);
      pendingReleases.set(templateId, timer);
    };
  }, [templateId, tabId]);

  // Cancel a pending release when the same workflow comes straight back (StrictMode's
  // double-invoke, or a route change that briefly unmounted the provider).
  //
  // Cancelling is ALL this does. It deliberately does not mark the lock as already held and
  // skip the acquire below: that would let a fresh session inherit "we hold it" from an
  // earlier one purely because a timer was still queued, without ever confirming with the
  // server. Letting the normal acquire run instead is self-healing and costs one cheap call,
  // since acquiring a lock you already hold is just a refresh.
  useEffect(() => {
    if (!templateId) return;
    const pending = pendingReleases.get(templateId);
    if (pending !== undefined) {
      window.clearTimeout(pending);
      pendingReleases.delete(templateId);
    }
  }, [templateId]);

  // Periodic lock-state poll while we hold the lock.
  //
  // The owner can reclaim a workflow at any time (DELETE /lock/force atomically clears
  // + re-acquires for them). A colleague who already has the editor open wouldn't find
  // out until they try to save — so we poll for it.
  //
  // KNOWN TRADEOFF: a 20s interval means up to 20 seconds of stale state. Bob can keep
  // typing for almost a full poll cycle after Alice reclaims, only to discover on his next
  // save that the BE has rejected him. The BE is the source of truth — his saves 409 — so
  // this never causes data corruption; it's purely a UX delay. The proper fix (sub-second
  // feedback) is push: a WebSocket / SSE channel broadcasting lock-displacement events.
  // That's a separate sprint; until then 20s is short enough that "huh, that failed" is
  // rare and long enough that a roomful of editors doesn't DoS the BE.
  useEffect(() => {
    if (!templateId || !workflow) return;
    if (lockBlock !== null) return;
    if (!workflow.lock?.heldByCaller) return;
    if (workflow.status !== 'Draft' && workflow.status !== 'Rejected') return;

    let cancelled = false;
    const tick = async () => {
      try {
        const fresh = await workflowsApi.get(templateId);
        if (cancelled) return;
        // BE computes heldByCaller for the requesting user; missing lock OR someone else
        // holding it means we lost our hold.
        //
        // Which of the two it is falls straight out of the payload: the BE omits locks that
        // have aged past the TTL, so a PRESENT foreign lock means a colleague actively holds
        // it (they reclaimed), and an ABSENT one means our own hold simply expired with nobody
        // stepping in. Same read-only outcome, opposite explanations to the user.
        if (!fresh.lock || !fresh.lock.heldByCaller) {
          setWorkflow(fresh);
          setLockBlock(
            fresh.lock
              ? {
                  reason: 'heldByOther',
                  holderName: fresh.lock.holderName ?? null,
                  expiresAt: fresh.lock.expiresAt ?? null,
                }
              : { reason: 'expired', holderName: null, expiresAt: null },
          );
          // Don't fire a stray release on unmount — we no longer hold it.
          acquiredLockRef.current = false;
          forgetEditingWorkflow(templateId, tabId);
        } else {
          setWorkflow(fresh);
        }
      } catch {
        // Best-effort — transient network errors don't bounce the user.
      }
    };
    const interval = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [templateId, workflow, lockBlock, tabId]);

  // Owner-only reclaim: BE atomically clears the foreign lock + acquires it for the calling
  // owner, so we go straight back into an editor session — no second acquire from the FE.
  const reclaim = useCallback(async () => {
    if (!templateId) return;
    try {
      await workflowsApi.forceReleaseLock(templateId);
      const fresh = await workflowsApi.get(templateId);
      setWorkflow(fresh);
      setLockBlock(null);
      acquiredLockRef.current = true;
      rememberEditingWorkflow(templateId, tabId);
    } catch (err) {
      handleApiError(err, t, t('workflow.lock.forceReleaseFailed'));
    }
  }, [templateId, tabId, t]);

  return (
    <WorkflowEditorSessionContext.Provider
      value={{ workflow, loading, lockBlock, reclaim, patchWorkflow: setWorkflow }}
    >
      <Outlet />
    </WorkflowEditorSessionContext.Provider>
  );
}

/**
 * Releases scheduled but not yet fired, keyed by workflow id. Module-scoped rather than a ref
 * because the whole point is to survive the unmount that scheduled them.
 */
const pendingReleases = new Map<string, number>();
